import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import LinkIcon from '@mui/icons-material/Link'

const URLInputStep = ({ urls, onUrlsChange, onSubmit, onCancel, isLoading }) => {
  const [errors, setErrors] = useState({})

  const validateUrl = (url) => {
    try {
      // If URL doesn't start with http/https, try adding https://
      if (url.startsWith('www.') || (!url.startsWith('http://') && !url.startsWith('https://'))) {
        const testUrl = url.startsWith('www.') ? `https://${url}` : `https://${url}`
        new URL(testUrl)
        return true
      }
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleUrlChange = (index, value) => {
    const newUrls = [...urls]
    newUrls[index] = value
    onUrlsChange(newUrls)
    
    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors }
      delete newErrors[index]
      setErrors(newErrors)
    }
  }

  const addUrl = () => {
    onUrlsChange([...urls, ''])
  }

  const removeUrl = (index) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index)
      onUrlsChange(newUrls)
      
      // Remove error for this field
      const newErrors = { ...errors }
      delete newErrors[index]
      setErrors(newErrors)
    }
  }

  const handleSubmit = () => {
    const newErrors = {}
    const validUrls = []

    urls.forEach((url, index) => {
      if (!url.trim()) {
        newErrors[index] = 'URL is required'
      } else if (!validateUrl(url.trim())) {
        newErrors[index] = 'Please enter a valid URL'
      } else {
        validUrls.push(url.trim())
      }
    })

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0 && validUrls.length > 0) {
      onSubmit(validUrls)
    }
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          Job Posting URLs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter URLs to job posting pages or career sites to extract individual job listings.
        </Typography>
      </Box>

      <Stack spacing={3}>
        {urls.map((url, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label={`URL ${index + 1}`}
              value={url}
              onChange={(e) => handleUrlChange(index, e.target.value)}
              error={!!errors[index]}
              helperText={errors[index]}
              placeholder="https://example.com/careers"
              disabled={isLoading}
              sx={{ flex: 1 }}
            />
            {urls.length > 1 && (
              <IconButton
                onClick={() => removeUrl(index)}
                disabled={isLoading}
                color="error"
                sx={{ mt: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}

        <Box>
          <Button
            startIcon={<AddIcon />}
            onClick={addUrl}
            disabled={isLoading}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Add Another URL
          </Button>
        </Box>

        {isLoading && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Analyzing URLs and extracting job postings... This may take a few moments.
              </Typography>
            </Box>
          </Alert>
        )}

        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'space-between',
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || urls.every(url => !url.trim())}
            size="large"
          >
            {isLoading ? 'Processing...' : 'Extract Jobs'}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export default URLInputStep
